import type { RouteEntry } from '../types';

// ═══════════════════════════════════════════════════════════════════════════
// PRICING + FCR + LIFE PROFIT TESTING
// ═══════════════════════════════════════════════════════════════════════════
export const PRICING_ROUTES: RouteEntry[] = [
  // ── Pricing ──
  {
    path: 'pricing',
    component: 'PricingDashboard',
    label: 'Dashboard',
    allowedRoles: [], // no ProtectedRoute wrapper — function dashboard
    functionality: 'pricing',
    navSection: 'Overview',
    navIcon: 'Dashboard',
    // MISMATCH: route has no role guard
  },
  {
    path: 'active-products',
    component: 'ActiveProductsPage',
    label: 'Product Catalog',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'pricing',
    navSection: 'Active Products',
    navIcon: 'Category',
    navRoles: ['consultant_user', 'super_admin'],
  },
  {
    path: 'active-products/commissions',
    component: 'ActiveProductCommissionsPage',
    label: '',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'pricing',
    hideFromNav: true,
    // MISMATCH: route exists but no nav item (sub-page of active-products)
  },
  {
    path: 'active-products/expenses',
    component: 'ActiveProductExpensesPage',
    label: '',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'pricing',
    hideFromNav: true,
    // MISMATCH: route exists but no nav item (sub-page of active-products)
  },
  {
    path: 'active-products/lapse-rates',
    component: 'ActiveProductLapseRatesPage',
    label: '',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'pricing',
    hideFromNav: true,
    // MISMATCH: route exists but no nav item (sub-page of active-products)
  },
  {
    path: 'pricing-products',
    component: 'ProductManagementPage',
    label: 'Products',
    allowedRoles: ['super_admin', 'admin', 'consultant_user', 'company_user'],
    functionality: 'pricing',
    navSection: 'Actuarial Pricing',
    navIcon: 'Category',
    // MISMATCH: nav has no roles (all) but route restricts to 4 roles
  },
  {
    path: 'pricing-analysis',
    component: 'PricingAnalysisPage',
    label: 'Analysis',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'pricing',
    navSection: 'Actuarial Pricing',
    navIcon: 'ShowChart',
    navRoles: ['super_admin', 'admin', 'consultant_user'],
  },
  {
    path: 'pricing-profit-test',
    component: 'ProfitTestPage',
    label: 'Profit Test',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'pricing',
    navSection: 'Actuarial Pricing',
    navIcon: 'Calculate',
    navRoles: ['super_admin', 'admin', 'consultant_user'],
  },
  {
    path: 'pricing-certificates',
    component: 'CertificatesPage',
    label: 'Certificates',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'pricing',
    navSection: 'Actuarial Pricing',
    navIcon: 'Article',
    navRoles: ['super_admin', 'admin', 'consultant_user'],
  },

  // ── Life Profit Testing (under pricing) ──
  {
    path: 'life-pricing',
    component: 'LifePricingDashboardPage',
    label: 'Dashboard',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'pricing',
    navSection: 'Life Profit Testing',
    navIcon: 'Dashboard',
    navRoles: ['super_admin', 'consultant_user'],
  },
  {
    path: 'life-pricing/model-points',
    component: 'LifeModelPointsPage',
    label: 'Model Points',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'pricing',
    navSection: 'Life Profit Testing',
    navIcon: 'People',
    navRoles: ['super_admin', 'consultant_user'],
  },
  {
    path: 'life-pricing/run',
    component: 'LifeProfitTestRunPage',
    label: 'Run Profit Test',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'pricing',
    navSection: 'Life Profit Testing',
    navIcon: 'Calculate',
    navRoles: ['super_admin', 'consultant_user'],
  },
  {
    path: 'life-pricing/history',
    component: 'LifeProfitTestHistoryPage',
    label: 'Saved Results',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'pricing',
    navSection: 'Life Profit Testing',
    navIcon: 'History',
    navRoles: ['super_admin', 'consultant_user'],
  },

  // ── Financial Condition Report (FCR) ──
  {
    path: 'financial-condition-report',
    component: 'FCRDashboard',
    label: '',
    allowedRoles: [], // no ProtectedRoute wrapper — function landing
    functionality: 'financial_condition_report',
    hideFromNav: true,
    // This is the function landing page (redirects to fcr-dashboard)
  },
  {
    path: 'fcr-dashboard',
    component: 'FCRDashboardPage',
    label: 'Dashboard',
    allowedRoles: ['super_admin', 'admin', 'consultant_user', 'company_user'],
    functionality: 'financial_condition_report',
    navSection: 'Overview',
    navIcon: 'Dashboard',
  },
  {
    path: 'fcr-report',
    component: 'FCRReportViewerPage',
    label: 'Full Report',
    allowedRoles: ['super_admin', 'admin', 'consultant_user', 'company_user'],
    functionality: 'financial_condition_report',
    navSection: 'Overview',
    navIcon: 'Description',
    // Also referenced by 8 nav items in 'Report Sections' all pointing to /fcr-report
  },

  // ── FCR Data Entry ──
  {
    path: 'fcr-financial-data',
    component: 'FCRFinancialDataPage',
    label: 'Financial Performance',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'financial_condition_report',
    navSection: 'Data Entry',
    navIcon: 'TrendingUp',
    navRoles: ['super_admin', 'consultant_user'],
    navCollapsible: true,
  },
  {
    path: 'fcr-business-mix',
    component: 'FCRBusinessMixPage',
    label: 'Business Mix',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'financial_condition_report',
    navSection: 'Data Entry',
    navIcon: 'PieChart',
    navRoles: ['super_admin', 'consultant_user'],
  },
  {
    path: 'fcr-capital',
    component: 'FCRCapitalPage',
    label: 'Capital Adequacy',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'financial_condition_report',
    navSection: 'Data Entry',
    navIcon: 'AccountBalance',
    navRoles: ['super_admin', 'consultant_user'],
  },
  {
    path: 'fcr-assets',
    component: 'FCRAssetAllocationPage',
    label: 'Asset Allocation',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'financial_condition_report',
    navSection: 'Data Entry',
    navIcon: 'Assessment',
    navRoles: ['super_admin', 'consultant_user'],
  },
  {
    path: 'fcr-reinsurance-data',
    component: 'FCRReinsuranceDataPage',
    label: 'Reinsurance Program',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'financial_condition_report',
    navSection: 'Data Entry',
    navIcon: 'Shield',
    navRoles: ['super_admin', 'consultant_user'],
  },
  {
    path: 'fcr-governance',
    component: 'FCRGovernancePage',
    label: 'Governance',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'financial_condition_report',
    navSection: 'Data Entry',
    navIcon: 'People',
    navRoles: ['super_admin', 'consultant_user'],
  },
  {
    path: 'fcr-recommendations',
    component: 'FCRRecommendationsPage',
    label: 'Recommendations',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'financial_condition_report',
    navSection: 'Data Entry',
    navIcon: 'Lightbulb',
    navRoles: ['super_admin', 'consultant_user'],
  },
  {
    path: 'fcr-risk-appetite',
    component: 'FCRRiskAppetitePage',
    label: 'Risk Appetite',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'financial_condition_report',
    navSection: 'Data Entry',
    navIcon: 'Warning',
    navRoles: ['super_admin', 'consultant_user'],
  },
  {
    path: 'fcr-narratives',
    component: 'FCRSectionNarrativesPage',
    label: 'Section Narratives',
    allowedRoles: ['super_admin', 'consultant_user'],
    functionality: 'financial_condition_report',
    navSection: 'Data Entry',
    navIcon: 'Article',
    navRoles: ['super_admin', 'consultant_user'],
  },

  // ── FCR Report Sections (all point to /fcr-report) ──
  {
    path: 'fcr-report',
    component: 'FCRReportViewerPage',
    label: 'Executive Summary',
    allowedRoles: ['super_admin', 'admin', 'consultant_user', 'company_user'],
    functionality: 'financial_condition_report',
    navSection: 'Report Sections',
    navIcon: 'Summarize',
    navOrder: 1,
  },
  {
    path: 'fcr-report',
    component: 'FCRReportViewerPage',
    label: 'Business Overview',
    allowedRoles: ['super_admin', 'admin', 'consultant_user', 'company_user'],
    functionality: 'financial_condition_report',
    navSection: 'Report Sections',
    navIcon: 'Business',
    navOrder: 2,
  },
  {
    path: 'fcr-report',
    component: 'FCRReportViewerPage',
    label: 'Profitability',
    allowedRoles: ['super_admin', 'admin', 'consultant_user', 'company_user'],
    functionality: 'financial_condition_report',
    navSection: 'Report Sections',
    navIcon: 'TrendingUp',
    navOrder: 3,
  },
  {
    path: 'fcr-report',
    component: 'FCRReportViewerPage',
    label: 'Reserves',
    allowedRoles: ['super_admin', 'admin', 'consultant_user', 'company_user'],
    functionality: 'financial_condition_report',
    navSection: 'Report Sections',
    navIcon: 'Assessment',
    navOrder: 4,
  },
  {
    path: 'fcr-report',
    component: 'FCRReportViewerPage',
    label: 'Capital Adequacy',
    allowedRoles: ['super_admin', 'admin', 'consultant_user', 'company_user'],
    functionality: 'financial_condition_report',
    navSection: 'Report Sections',
    navIcon: 'AccountBalance',
    navOrder: 5,
  },
  {
    path: 'fcr-report',
    component: 'FCRReportViewerPage',
    label: 'Reinsurance',
    allowedRoles: ['super_admin', 'admin', 'consultant_user', 'company_user'],
    functionality: 'financial_condition_report',
    navSection: 'Report Sections',
    navIcon: 'Shield',
    navOrder: 6,
  },
  {
    path: 'fcr-report',
    component: 'FCRReportViewerPage',
    label: 'Risk Management',
    allowedRoles: ['super_admin', 'admin', 'consultant_user', 'company_user'],
    functionality: 'financial_condition_report',
    navSection: 'Report Sections',
    navIcon: 'Warning',
    navOrder: 7,
  },
  {
    path: 'fcr-report',
    component: 'FCRReportViewerPage',
    label: 'Recommendations',
    allowedRoles: ['super_admin', 'admin', 'consultant_user', 'company_user'],
    functionality: 'financial_condition_report',
    navSection: 'Report Sections',
    navIcon: 'Lightbulb',
    navOrder: 8,
  },

  // ── FCR Tools ──
  {
    path: 'fcr-solvency',
    component: 'SolvencyAnalysisPage',
    label: 'Solvency Analysis',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'financial_condition_report',
    navSection: 'Tools',
    navIcon: 'AccountBalance',
    navRoles: ['super_admin', 'admin', 'consultant_user'],
  },
  {
    path: 'fcr-risk-heatmap',
    component: 'RiskHeatmapPage',
    label: 'Risk Heatmap',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'financial_condition_report',
    navSection: 'Tools',
    navIcon: 'Shield',
    navRoles: ['super_admin', 'admin', 'consultant_user'],
  },
  {
    path: 'fcr-stress-test',
    component: 'FCRStressTestPage',
    label: 'Stress Testing',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'financial_condition_report',
    navSection: 'Tools',
    navIcon: 'ShowChart',
    navRoles: ['super_admin', 'admin', 'consultant_user'],
  },
  {
    path: 'fcr-reconciliation',
    component: 'FCRReconciliationPage',
    label: 'Data Reconciliation',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'financial_condition_report',
    navSection: 'Tools',
    navIcon: 'CompareArrows',
    navRoles: ['super_admin', 'admin', 'consultant_user'],
  },
];
